/**
 * Measured head pose for the gaze calibration — shared by the admin lab and
 * the respondent calibration.
 *
 * Only MediaPipe Face Landmarker runs here (via GazeTracker); none of
 * Miłosz's weights are involved. The gaze itself is computed server-side.
 *
 * Angles come from MediaPipe's facial transformation matrix (gazeFeatures:
 * base[6] = yaw, base[7] = pitch, radians, raw un-mirrored camera). Sign
 * convention, same as Miłosz's configs/session.yaml: yaw > 0 = head turned to
 * the user's LEFT, pitch > 0 = head tilted DOWN.
 *
 * Targets are relative to a baseline measured on the first "center" pose, not
 * to absolute zero: the webcam usually sits above the screen, so looking
 * straight at the screen already reads as a few degrees of pitch.
 */
import type { HeadPose } from '@/lib/calibrationVariants'

export const POSE_TARGET_DEG: Record<HeadPose, { yaw: number; pitch: number }> = {
  center: { yaw: 0, pitch: 0 },
  left: { yaw: 15, pitch: 0 },
  right: { yaw: -15, pitch: 0 },
  top: { yaw: 0, pitch: -15 },
  bottom: { yaw: 0, pitch: 15 },
}

// Miłosz uses 3° with a half-width entry band; MediaPipe's in-browser pose
// jitters more than his desktop pipeline, so the band is a bit wider here.
/** Max deviation from the pose target while a point is collecting. */
export const POSE_TOLERANCE_DEG = 5
/** Tighter band the head must enter (and hold) to (re)start a pose. */
export const POSE_ENTER_DEG = 3
export const POSE_HOLD_MS = 600
/** While the baseline doesn't exist yet: the head must be roughly frontal. */
export const BASELINE_MAX_YAW = 15
export const BASELINE_MAX_PITCH = 25

export interface HeadDeg {
  yaw: number
  pitch: number
}

const RAD = 180 / Math.PI
export const headFromBase = (base: number[]): HeadDeg => ({ yaw: base[6] * RAD, pitch: base[7] * RAD })

/** Head angle minus where the pose wants it (positive yaw = too far left). */
export function poseOffset(head: HeadDeg, baseline: HeadDeg | null, pose: HeadPose): HeadDeg {
  const target = POSE_TARGET_DEG[pose]
  const zero = baseline ?? { yaw: 0, pitch: 0 }
  return {
    yaw: head.yaw - zero.yaw - target.yaw,
    pitch: head.pitch - zero.pitch - target.pitch,
  }
}

export const within = (off: HeadDeg, tol: number) => Math.abs(off.yaw) <= tol && Math.abs(off.pitch) <= tol

export const clamp01 = (v: number) => Math.min(1, Math.max(0, v))

/** What the pose guidance screen renders (throttled copy of per-frame refs). */
export interface PoseView {
  face: boolean
  /** Offset from target, degrees; null until a face is seen. */
  off: HeadDeg | null
  inside: boolean
  progress: number
  /** True while the "center" baseline is still being measured. */
  establishing: boolean
}

export const EMPTY_POSE_VIEW: PoseView = { face: false, off: null, inside: false, progress: 0, establishing: true }

/**
 * Pose acquisition as a small state machine: feed it one head reading per
 * camera frame and it reports when the head has held the target for
 * POSE_HOLD_MS. The first acquisition (no baseline yet) accepts any roughly
 * frontal, still head and takes its mean as the zero for every later pose —
 * the same rule as the lab's runPoseStage.
 */
export class PoseHold {
  private holdStart: number | null = null
  private samples: HeadDeg[] = []

  reset(): void {
    this.holdStart = null
    this.samples = []
  }

  /** One frame. `baseline` null = this hold establishes the baseline. */
  update(
    head: HeadDeg | null,
    baseline: HeadDeg | null,
    pose: HeadPose,
    t: number,
  ): { view: PoseView; done: boolean; baseline: HeadDeg | null } {
    let inside = false
    let off: HeadDeg | null = null
    if (head) {
      if (baseline === null) {
        off = poseOffset(head, null, 'center')
        const frontal = Math.abs(head.yaw) <= BASELINE_MAX_YAW && Math.abs(head.pitch) <= BASELINE_MAX_PITCH
        const anchor = this.samples[0]
        inside =
          frontal &&
          (!anchor || within({ yaw: head.yaw - anchor.yaw, pitch: head.pitch - anchor.pitch }, POSE_ENTER_DEG))
      } else {
        off = poseOffset(head, baseline, pose)
        inside = within(off, POSE_ENTER_DEG)
      }
    }

    if (inside && head) {
      if (this.holdStart === null) this.holdStart = t
      this.samples.push(head)
    } else {
      this.reset()
    }
    const progress = this.holdStart === null ? 0 : clamp01((t - this.holdStart) / POSE_HOLD_MS)
    const view: PoseView = { face: head !== null, off, inside, progress, establishing: baseline === null }
    if (progress < 1) return { view, done: false, baseline }

    let next = baseline
    if (next === null) {
      const n = this.samples.length
      next = {
        yaw: this.samples.reduce((a, h) => a + h.yaw, 0) / n,
        pitch: this.samples.reduce((a, h) => a + h.pitch, 0) / n,
      }
    }
    this.reset()
    return { view, done: true, baseline: next }
  }
}
